import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { MapPin, Phone, CheckCircle, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { useLanguage } from "../contexts/LanguageContext";

const ZonesInterventionPage = () => {
  const { t } = useLanguage();

  const regions = [
    {
      name: "Bruxelles-Capitale",
      description: "Nous couvrons l'ensemble de la Région de Bruxelles-Capitale avec des équipes dédiées disponibles 7j/7.",
      cities: [
        "Bruxelles-Ville", "Ixelles", "Saint-Gilles", "Uccle", "Watermael-Boitsfort",
        "Auderghem", "Woluwe-Saint-Pierre", "Woluwe-Saint-Lambert", "Etterbeek", "Schaerbeek",
        "Evere", "Saint-Josse-ten-Noode", "Molenbeek-Saint-Jean", "Koekelberg", "Berchem-Sainte-Agathe",
        "Ganshoren", "Jette", "Anderlecht", "Forest"
      ],
      color: "from-blue-600 to-blue-800",
      highlight: true
    },
    {
      name: "Brabant wallon",
      description: "Intervention rapide dans tout le Brabant wallon pour entreprises et copropriétés.",
      cities: [
        "Wavre", "Ottignies-Louvain-la-Neuve", "Waterloo", "Braine-l'Alleud", "Nivelles",
        "Rixensart", "La Hulpe", "Lasne", "Tubize", "Jodoigne", "Perwez", "Genappe"
      ],
      color: "from-emerald-600 to-emerald-800"
    },
    {
      name: "Wallonie - Province de Liège",
      description: "Services de nettoyage professionnel dans la province de Liège et ses environs.",
      cities: [
        "Liège", "Seraing", "Herstal", "Verviers", "Visé", "Ans", "Saint-Nicolas",
        "Grâce-Hollogne", "Flémalle", "Chaudfontaine", "Eupen", "Malmedy"
      ],
      color: "from-red-600 to-red-800"
    },
    {
      name: "Wallonie - Province de Namur",
      description: "Couverture complète de la province de Namur pour tous vos besoins en nettoyage.",
      cities: [
        "Namur", "Sambreville", "Gembloux", "Andenne", "Ciney", "Dinant",
        "Profondeville", "Fosses-la-Ville", "Rochefort", "Couvin"
      ],
      color: "from-amber-600 to-amber-800"
    },
    {
      name: "Wallonie - Province du Hainaut",
      description: "Présence forte dans le Hainaut avec des équipes locales réactives.",
      cities: [
        "Charleroi", "Mons", "La Louvière", "Tournai", "Mouscron", "Binche",
        "Soignies", "Châtelet", "Manage", "Braine-le-Comte", "Ath", "Lessines"
      ],
      color: "from-purple-600 to-purple-800"
    },
    {
      name: "Brabant flamand",
      description: "Services de nettoyage professionnel dans le Brabant flamand.",
      cities: [
        "Louvain (Leuven)", "Vilvorde", "Hal", "Zaventem", "Tervuren",
        "Diest", "Tirlemont (Tienen)", "Aarschot", "Overijse", "Hoeilaart"
      ],
      color: "from-slate-600 to-slate-800"
    }
  ];

  const services = [
    "Nettoyage de bureaux",
    "Nettoyage industriel",
    "Entretien d'immeubles",
    "Nettoyage après travaux",
    "Désinfection",
    "Nettoyage de vitres"
  ];

  // Structured data for local business with service areas
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Nettis Facility",
    "description": "Services de nettoyage professionnel en Belgique - Bruxelles, Wallonie, Brabant",
    "url": "https://www.nettis.be",
    "telephone": "+32 2 880 66 50",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Avenue Louise 65 boîte 11",
      "addressLocality": "Bruxelles",
      "postalCode": "1050",
      "addressCountry": "BE"
    },
    "areaServed": [
      { "@type": "City", "name": "Bruxelles" },
      { "@type": "State", "name": "Wallonie" },
      { "@type": "State", "name": "Brabant wallon" },
      { "@type": "State", "name": "Brabant flamand" },
      { "@type": "City", "name": "Liège" },
      { "@type": "City", "name": "Namur" },
      { "@type": "City", "name": "Charleroi" }
    ]
  };

  return (
    <div className="pt-20">
      <SEO
        title="Zones d'intervention - Nettoyage Bruxelles, Wallonie, Brabant"
        description="Nettis Facility intervient dans toute la Belgique : Bruxelles et ses 19 communes, Wallonie (Liège, Namur, Charleroi), Brabant wallon et flamand. Devis gratuit pour votre région."
        keywords="nettoyage Bruxelles, société nettoyage Liège, nettoyage bureaux Namur, entretien immeubles Charleroi, nettoyage Waterloo, nettoyage Wavre, nettoyage Ixelles, nettoyage Uccle"
        canonicalUrl="https://www.nettis.be/zones-intervention"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-[#10B981] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-full mb-6">
              <MapPin className="w-4 h-4" /> Couverture nationale
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nos zones <span className="text-[#10B981]">d'intervention</span>
            </h1>
            <p className="text-lg text-blue-100 leading-relaxed mb-8">
              Nettis Facility intervient dans toute la Belgique. Bruxelles, Wallonie, Brabant wallon et flamand : 
              nos équipes sont proches de vous pour un service de nettoyage professionnel de qualité.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {["Bruxelles", "Liège", "Namur", "Charleroi", "Waterloo"].map((city) => (
                <span key={city} className="px-4 py-2 bg-white/10 rounded-full text-sm">
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Regions Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-4">
              Intervention dans toute la <span className="text-[#1E3A8A]">Belgique</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Découvrez nos zones de couverture. Nos équipes locales garantissent une intervention rapide et un suivi de proximité.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regions.map((region, index) => (
              <div 
                key={index} 
                className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow ${region.highlight ? 'md:col-span-2 lg:col-span-1' : ''}`}
              >
                <div className={`bg-gradient-to-r ${region.color} p-6 text-white`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-5 h-5" />
                    <h3 className="text-xl font-bold">{region.name}</h3>
                  </div>
                  <p className="text-white/80 text-sm">{region.description}</p>
                </div>
                <div className="p-6 bg-slate-50">
                  <p className="text-sm font-medium text-slate-700 mb-3">Villes couvertes :</p>
                  <div className="flex flex-wrap gap-2">
                    {region.cities.map((city, cityIndex) => (
                      <span 
                        key={cityIndex}
                        className="px-3 py-1 bg-white rounded-full text-xs text-slate-600 border border-slate-200"
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services disponibles */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">
                Services disponibles dans <span className="text-[#10B981]">toutes nos zones</span>
              </h2>
              <p className="text-slate-600 mb-8">
                Quel que soit votre emplacement en Belgique, vous bénéficiez de notre gamme complète de services de nettoyage professionnel.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#10B981]" />
                    <span className="text-slate-700">{service}</span>
                  </div>
                ))}
              </div>
              <Link to="/services" className="inline-block mt-8">
                <Button className="bg-[#1E3A8A] hover:bg-[#2563EB] text-white rounded-full px-6">
                  Voir tous nos services <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-[#0F172A] mb-4">Demandez un devis gratuit</h3>
              <p className="text-slate-600 mb-6">
                Vous êtes dans une de nos zones d'intervention ? Contactez-nous pour un devis personnalisé et gratuit.
              </p>
              <div className="space-y-4">
                <a href="tel:+3228806650" className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Appelez-nous</p>
                    <p className="font-semibold text-[#0F172A]">+32 2 880 66 50</p>
                  </div>
                </a>
                <Link to="/contact" className="block">
                  <Button className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-full py-3">
                    Demander un devis gratuit
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#10B981] to-[#059669]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Votre ville n'est pas listée ?
          </h2>
          <p className="text-green-100 mb-8 max-w-2xl mx-auto">
            Nous intervenons également dans d'autres localités en Belgique. Contactez-nous pour vérifier notre disponibilité dans votre région.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#10B981] hover:bg-green-50 rounded-full px-8 py-3 font-semibold">
              Nous contacter
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ZonesInterventionPage;
